import {EditOutlined} from '@ant-design/icons/lib';
import {Button, Card, Space} from 'antd';
import {
  BillingPlans,
  Currencies,
  TransactionTypes,
  UpdateAccountInput,
  useNotificationsPortalUrlQuery
} from 'api/generated';
import {AnchorLink, Hint, PropertyList, Stack} from 'components';
import {Formik} from 'formik';
import {Form, FormItem, Select, SubmitButton, Switch} from 'formik-antd';
import {useAccessLevel} from 'hooks/useAccessLevel';
import {formatBoolean, formatWebsite} from 'lib/formaters';
import {isUndefined} from 'lib/utils';
import React, {useState} from 'react';
import styled from 'styled-components';
import * as yup from 'yup';
import {UrlInput} from '../../components/Form/UrlInput';
import {SectionProps} from './types';

const {Option} = Select;

const transactionTypeLabels: Record<string, string> = {
  [TransactionTypes.SALE]: 'Direct sale',
  [TransactionTypes.AUTH]: 'Authorization'
};

const NotificationsPortalUrl = styled(AnchorLink)`
  display: flex;
  padding-top: 4px;
  padding-bottom: 4px;
  flex-direction: row;
`;

export const AccountSettings: React.FC<SectionProps> = ({
  cardProps,
  formProps,
  account,
  accountId,
  onSubmit
}) => {
  const [isEditing, setEditing] = useState(false);
  const {isAdmin} = useAccessLevel();

  const {data} = useNotificationsPortalUrlQuery({
    variables: {accountId}
  });

  if (!account) return <Card {...cardProps} title="Account details" />;

  const validationSchema = yup.object().shape({
    customDomain: yup
      .string()
      .nullable()
      .test('checkCustomDomain', 'this domain is not configured yet', async (value) => {
        if (!value) return true;
        try {
          const res = await fetch(`https://${value}/v1/payment-methods?accountId=${account.id}`);
          return res.status === 401 || res.status === 200;
        } catch (error) {
          return false;
        }
      })
  });

  const initialValues: UpdateAccountInput = {
    id: account.id,
    reviewConfirmed: account.reviewConfirmed || false,
    defaultCurrency: account.defaultCurrency,
    defaultTransactionType: account.defaultTransactionType,
    customDomain: account.customDomain,
    notifications: account.notifications || {},
    allowCreateApiKey: account.allowCreateApiKey || false,
    allowRefunds: isUndefined(account.allowRefunds) ? true : account.allowRefunds
  };

  return (
    <Card
      {...cardProps}
      title="Settings"
      extra={
        isEditing || !isAdmin ? null : (
          <AnchorLink onClick={() => setEditing(true)}>
            <EditOutlined /> Edit data
          </AnchorLink>
        )
      }>
      {isEditing ? (
        <Formik
          validationSchema={validationSchema}
          validateOnChange={false}
          onSubmit={async (values) => {
            await onSubmit(values);
            setEditing(false);
          }}
          initialValues={initialValues}>
          <Form {...formProps}>
            <FormItem name="defaultCurrency" label="Default currency">
              <Select name="defaultCurrency">
                {Object.values(Currencies).map((currency) => (
                  <Option key={currency} value={currency}>
                    {currency}
                  </Option>
                ))}
              </Select>
            </FormItem>
            <FormItem
              name="defaultTransactionType"
              label="Default transaction type"
              extra="Set it to Authorization to allow delayed manual in shopify">
              <Select name="defaultTransactionType">
                <Option key={TransactionTypes.AUTH} value={TransactionTypes.AUTH}>
                  Authorization
                </Option>
                <Option key={TransactionTypes.SALE} value={TransactionTypes.SALE}>
                  Direct sale
                </Option>
              </Select>
            </FormItem>
            <FormItem name="customDomain" label="Custom domain">
              <UrlInput name="customDomain" placeholder="secure.monei.com" />
            </FormItem>
            <FormItem
              name="notifications.emailCustomerOnSuccess"
              label={
                <Space>
                  <span>Email customer on successful payment</span>
                  <Hint message="Merchant can disable this option only on MONEI PLUS plan" />
                </Space>
              }>
              <Switch name="notifications.emailCustomerOnSuccess" />
            </FormItem>
            <FormItem
              name="notifications.emailCustomerOnRefund"
              label={
                <Space>
                  <span>Email customer on refund</span>
                  <Hint message="Merchant can disable this option only on MONEI PLUS plan" />
                </Space>
              }>
              <Switch name="notifications.emailCustomerOnRefund" />
            </FormItem>
            <FormItem
              name="notifications.emailAdminOnSuccess"
              label={
                <Space>
                  <span>Email admin on successful payment</span>
                </Space>
              }>
              <Switch name="notifications.emailAdminOnSuccess" />
            </FormItem>
            <FormItem
              name="notifications.emailCustomerOnSubscription"
              label={
                <Space>
                  <span>Email customer on subscription</span>
                </Space>
              }>
              <Switch name="notifications.emailCustomerOnSubscription" />
            </FormItem>
            <FormItem
              name="notifications.emailAdminOnSubscription"
              label={
                <Space>
                  <span>Email admin on subscription</span>
                </Space>
              }>
              <Switch name="notifications.emailAdminOnSubscription" />
            </FormItem>
            <FormItem
              name="allowRefunds"
              label={
                <Space>
                  <span>Allow refunds</span>
                </Space>
              }>
              <Switch name="allowRefunds" />
            </FormItem>
            {account.billingPlan === BillingPlans.MONEI_POS && (
              <FormItem
                name="allowCreateApiKey"
                label={
                  <Space>
                    <span>Allow create api key</span>
                    <Hint message="By default merchants on MONEI POS plan are not allowed to create API keys" />
                  </Space>
                }>
                <Switch name="allowCreateApiKey" />
              </FormItem>
            )}
            <FormItem name="reviewConfirmed" label="Review confirmed">
              <Switch name="reviewConfirmed" />
            </FormItem>
            <FormItem
              wrapperCol={{offset: formProps.labelCol?.span, ...formProps.wrapperCol}}
              name="submit">
              <Stack>
                <Button onClick={() => setEditing(false)}>Cancel</Button>
                <SubmitButton>Submit</SubmitButton>
              </Stack>
            </FormItem>
          </Form>
        </Formik>
      ) : (
        <>
          <PropertyList
            omitEmpty
            labelWidth={280}
            properties={[
              {
                label: 'Default currency',
                value: account.defaultCurrency
              },
              {
                label: 'Default transaction type',
                value:
                  transactionTypeLabels[account.defaultTransactionType || TransactionTypes.SALE]
              },
              {
                label: 'Custom domain',
                value: formatWebsite(account.customDomain)
              },
              {
                label: 'Email customer on success',
                value: formatBoolean(account.notifications?.emailCustomerOnSuccess)
              },
              {
                label: 'Email customer on refund',
                value: formatBoolean(account.notifications?.emailCustomerOnRefund)
              },
              {
                label: 'Email admin on successful transactions',
                value: formatBoolean(account.notifications?.emailAdminOnSuccess)
              },
              {
                label: 'Email customer on subscription',
                value: formatBoolean(account.notifications?.emailCustomerOnSubscription)
              },
              {
                label: 'Email admin on subscription',
                value: formatBoolean(account.notifications?.emailAdminOnSubscription)
              },
              {
                label: 'Allow refunds',
                value: formatBoolean(
                  isUndefined(account.allowRefunds) ? true : account.allowRefunds
                )
              },
              ...(account.billingPlan === BillingPlans.MONEI_POS
                ? [
                    {
                      label: 'Allow create api key',
                      value: formatBoolean(account.allowCreateApiKey)
                    }
                  ]
                : []),
              ...(isAdmin
                ? [
                    {
                      label: 'Review',
                      value: account.reviewConfirmed
                        ? formatBoolean(account.reviewConfirmed, 'Confirmed')
                        : null
                    }
                  ]
                : [])
            ]}
          />
          <NotificationsPortalUrl
            disabled={!data}
            href={data ? data!.notificationsPortalUrl?.redirectUrl : ''}
            target="_blank">
            Marketing email preferences
          </NotificationsPortalUrl>
        </>
      )}
    </Card>
  );
};
